import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { Chip } from 'src/components/shared/Chip'
import { SectionCard } from 'src/components/shared/SectionCard'
import { semanticColors } from 'src/constants/semanticTokens'
import type { OatfiBusiness } from 'src/interfaces/oatfiBusiness'

interface Props {
  business: OatfiBusiness | null
  // `oatfi_manage`. Without it the card only reports the status chip.
  canCreate: boolean
  isCreating: boolean
  onCreate(): void
}

const OatfiPaymentMethodCardComponent = ({ business, canCreate, isCreating, onCreate }: Props) => {
  const isCreated = Boolean(business?.paymentMethodExternalId)

  const statusChip = isCreated ? (
    <Chip
      text="Created"
      className={{ backgroundColor: semanticColors.feedback.successBg }}
      textClassName={{ color: semanticColors.feedback.successFg }}
      dataTestId="oatfi-payment-method-chip"
    />
  ) : (
    <Chip
      text="Not created"
      className={tw`bg-shades-100`}
      textClassName={tw`text-shades-500`}
      dataTestId="oatfi-payment-method-chip"
    />
  )

  return (
    <SectionCard
      title="Oatfi payment method"
      subtitle="This registers the customer's existing US Bank payment method with the lender. There is nothing to fill in."
      actions={statusChip}
      dataTestId="admin-customer-oatfi-payment-method-card"
    >
      {isCreated ? (
        // A second create is a 400 ('Business payment method already exists'), and the update
        // endpoint (PUT .../payment-methods) is deliberately out of scope for ENG-482 — so
        // there is no action to offer here. A customer whose bank account changes needs that
        // follow-up before this card can repoint them.
        <Typography variant="body2" css={tw`text-shades-400`}>
          Already registered with the lender. Changing it is not yet supported here.
        </Typography>
      ) : null}
      {!isCreated && canCreate ? (
        <Fragment>
          {/* Retool's own red note, kept as real copy: it is the only hint that the fix for the
              two commonest failures (no partner-stitch row, no partner wallet) lives on the
              Payments side rather than on this tab. */}
          <Typography variant="body2" css={tw`text-shades-500`}>
            This only works if a US Bank payment method is already set up for the customer.
          </Typography>
          <div css={tw`flex flex-row justify-end`}>
            <Button
              size="small"
              color="dark"
              className={tw`px-4`}
              onClick={onCreate}
              disabled={business === null || isCreating}
              data-testid="oatfi-payment-method-create"
            >
              Create payment method
            </Button>
          </div>
        </Fragment>
      ) : null}
    </SectionCard>
  )
}

export const OatfiPaymentMethodCard = memo(OatfiPaymentMethodCardComponent)
